import { useMemo, useState } from "react";
import { SurveyForm, SurveyHeader, type SurveySubmitResult } from "@legal-500/survey-renderer";
import { Select, Icon } from "@legal-500/legal500-design-system";
import { randomLibrary } from "@/api/random-library.service";
import type { LibraryQuestion } from "@/api/survey-library.service";
import type { DisplayRuleOptions } from "@/api/surveyTaxonomy.service";
import { useQuery } from "@/hooks/useApi";
import type { EditableSurveyState, SurveyDefinition } from "./surveyDetails.types";
import {
  appendRandomQuestionForPreview,
  getRandomQuestionOptionLabel,
  libraryQuestionToSurveyJsElement,
} from "./randomQuestionPreview.utils";
import styles from "./SurveyPreviewTab.module.css";

export type PreviewContext = {
  refereeBusinessType: string;
  submissionType: string;
  refereeType: string;
  region: string;
  practiceGroup: string;
  practiceArea: string;
};

export const EMPTY_CONTEXT: PreviewContext = {
  refereeBusinessType: "",
  submissionType: "",
  refereeType: "",
  region: "",
  practiceGroup: "",
  practiceArea: "",
};

const REFEREE_BUSINESS_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "Instructing solicitor", label: "Instructing solicitor" },
  { value: "In-house counsel", label: "In-house counsel" },
  { value: "Other", label: "Other" },
];

const SUBMISSION_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "Firm", label: "Firm" },
  { value: "Lawyer", label: "Lawyer" },
];

const REFEREE_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "Firm", label: "Firm" },
  { value: "Lawyer", label: "Lawyer" },
  { value: "Barrister", label: "Barrister" },
  { value: "Mediator", label: "Mediator" },
];

const FIELD_LABELS: Record<keyof PreviewContext, string> = {
  refereeBusinessType: "Business Type",
  submissionType: "Submission",
  refereeType: "Referee Type",
  region: "Region",
  practiceGroup: "Practice Group",
  practiceArea: "Practice Area",
};

type SurveyPreviewTabProps = {
  definition: SurveyDefinition;
  survey: EditableSurveyState | null;
  taxonomyOptions?: DisplayRuleOptions;
  previewContext: PreviewContext;
  onPreviewContextChange: (ctx: PreviewContext) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
};

async function previewSubmitAction(): Promise<SurveySubmitResult> {
  return { success: true, submittedAt: new Date().toISOString() };
}

function filterDefinitionForPreview(
  definition: SurveyDefinition,
  context: PreviewContext,
): SurveyDefinition {
  const pages = (definition.pages ?? []).map(page => ({
    ...page,
    elements: (page.elements ?? []).filter(element => {
      const rules = element.displayRules;
      if (!rules || rules.length === 0) return true;
      return rules.some(rule => {
        const filters = rule.submissionFilters;
        if (!filters) return true;
        for (const [key, value] of Object.entries(filters)) {
          if (!value) continue;
          const contextValue = context[key as keyof PreviewContext];
          if (contextValue && contextValue !== value) return false;
        }
        return true;
      });
    }),
  }));
  return { ...definition, pages };
}

function getValueLabel(
  field: keyof PreviewContext,
  value: string,
  taxonomyOptions: DisplayRuleOptions | undefined,
): string {
  if (field === "region" || field === "practiceGroup" || field === "practiceArea") {
    return taxonomyOptions?.[field].find(o => o.value === value)?.label ?? value;
  }
  return value;
}

export function SurveyPreviewTab({
  definition,
  survey,
  taxonomyOptions,
  previewContext,
  onPreviewContextChange,
  collapsed,
  onCollapsedChange,
}: SurveyPreviewTabProps) {
  const [previewRandomQuestionId, setPreviewRandomQuestionId] = useState("");

  const {
    data: previewRandomQuestions,
    loading: randomQuestionsLoading,
    error: randomQuestionsError,
  } = useQuery(() => randomLibrary.listAll(), []);

  const randomQuestionsById = useMemo(() => {
    const map = new Map<string, LibraryQuestion>();
    for (const question of previewRandomQuestions ?? []) {
      map.set(question.id, question);
    }
    return map;
  }, [previewRandomQuestions]);

  const randomQuestionSelectOptions = useMemo(() => {
    const questions = [...(previewRandomQuestions ?? [])].sort((a, b) =>
      getRandomQuestionOptionLabel(a).localeCompare(getRandomQuestionOptionLabel(b)),
    );
    return [
      { value: "", label: "None" },
      ...questions.map(question => ({
        value: question.id,
        label: getRandomQuestionOptionLabel(question),
      })),
    ];
  }, [previewRandomQuestions]);

  const filteredDefinition = filterDefinitionForPreview(definition, previewContext);

  const previewDefinitionWithRandom = useMemo(() => {
    if (!previewRandomQuestionId) {
      return filteredDefinition;
    }
    const question = randomQuestionsById.get(previewRandomQuestionId);
    if (!question) {
      return filteredDefinition;
    }
    const element = libraryQuestionToSurveyJsElement(question);
    return appendRandomQuestionForPreview(filteredDefinition, element);
  }, [filteredDefinition, previewRandomQuestionId, randomQuestionsById]);

  const surveyFormKey = [
    previewDefinitionWithRandom.pages.flatMap(p => p.elements.map(e => e.name)).join(","),
    previewRandomQuestionId,
  ].join("|");

  const hasContent = (previewDefinitionWithRandom.pages ?? []).some(
    page => (page.elements ?? []).length > 0,
  );

  const setField = (field: keyof PreviewContext) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    onPreviewContextChange({ ...previewContext, [field]: e.target.value });
  };

  const clearField = (field: keyof PreviewContext) => {
    onPreviewContextChange({ ...previewContext, [field]: "" });
  };

  const activeFilters = (Object.keys(previewContext) as (keyof PreviewContext)[]).filter(
    field => previewContext[field] !== "",
  );

  const practiceAreaSelectOptions = useMemo(() => {
    if (!taxonomyOptions) return [];
    if (previewContext.region === "") return taxonomyOptions.practiceArea;
    return taxonomyOptions.practiceArea
      .filter(opt => opt.region === previewContext.region)
      .map(opt => ({ ...opt, label: opt.baseName }));
  }, [taxonomyOptions, previewContext.region]);

  const publication =
    typeof survey?.publication === "string" && survey.publication.length > 0
      ? survey.publication
      : "Publication";
  const firmName =
    typeof survey?.name === "string" && survey.name.length > 0 ? survey.name : "Sample firm";

  const originalHasContent = (definition.pages ?? []).some(
    page => (page.elements ?? []).length > 0,
  );

  if (!originalHasContent) {
    return (
      <div className={styles.previewContainer}>
        <div className={styles.empty}>
          Add at least one question in the Content tab to see a preview.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.previewContainer}>
      <div className={styles.filterBar}>
        <div className={styles.filterBarHeader}>
          <span className={styles.filterBarTitle}>Context filters</span>
          <button
            type="button"
            className={styles.filterToggle}
            onClick={() => onCollapsedChange(!collapsed)}
          >
            <Icon icon={collapsed ? "chevron-down" : "chevron-up"} size="small" />
          </button>
        </div>

        <div className={styles.previewRandomRow}>
          <Select
            label="Random question"
            options={randomQuestionSelectOptions}
            value={previewRandomQuestionId}
            onChange={event => setPreviewRandomQuestionId(event.target.value)}
            disabled={randomQuestionsLoading}
          />
          <p className={styles.previewRandomHint}>
            {randomQuestionsError
              ? `Could not load random questions: ${randomQuestionsError.message}`
              : randomQuestionsLoading
                ? "Loading random questions…"
                : "Shown as the last question in the preview. Not saved with the survey."}
          </p>
        </div>

        {collapsed ? (
          <div className={styles.filterChipRow}>
            {activeFilters.length === 0 ? (
              <span className={styles.noFilters}>No filters active</span>
            ) : (
              activeFilters.map(field => (
                <span key={field} className={styles.filterChip}>
                  <span className={styles.filterChipLabel}>{FIELD_LABELS[field]}:</span>
                  <span className={styles.filterChipValue}>
                    {getValueLabel(field, previewContext[field], taxonomyOptions)}
                  </span>
                  <button
                    type="button"
                    className={styles.filterChipRemove}
                    onClick={() => clearField(field)}
                    aria-label={`Remove ${FIELD_LABELS[field]} filter`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
            {activeFilters.length > 0 && (
              <button
                type="button"
                className={styles.filterBarReset}
                onClick={() => onPreviewContextChange(EMPTY_CONTEXT)}
              >
                Reset
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.filterRow}>
              <Select
                label="Business Type"
                options={REFEREE_BUSINESS_TYPE_OPTIONS}
                value={previewContext.refereeBusinessType}
                onChange={setField("refereeBusinessType")}
              />
              <Select
                label="Submission"
                options={SUBMISSION_TYPE_OPTIONS}
                value={previewContext.submissionType}
                onChange={setField("submissionType")}
              />
              <Select
                label="Referee Type"
                options={REFEREE_TYPE_OPTIONS}
                value={previewContext.refereeType}
                onChange={setField("refereeType")}
              />
            </div>
            <div className={styles.filterRow}>
              {taxonomyOptions && (
                <>
                  <Select
                    label="Region"
                    options={[{ value: "", label: "Any" }, ...taxonomyOptions.region]}
                    value={previewContext.region}
                    onChange={setField("region")}
                  />
                  <Select
                    label="Practice Group"
                    options={[{ value: "", label: "Any" }, ...taxonomyOptions.practiceGroup]}
                    value={previewContext.practiceGroup}
                    onChange={setField("practiceGroup")}
                  />
                  <Select
                    label="Practice Area"
                    options={[{ value: "", label: "Any" }, ...practiceAreaSelectOptions]}
                    value={previewContext.practiceArea}
                    onChange={setField("practiceArea")}
                  />
                </>
              )}
              <button
                type="button"
                className={styles.filterBarReset}
                onClick={() => onPreviewContextChange(EMPTY_CONTEXT)}
              >
                Reset
              </button>
            </div>
          </>
        )}
      </div>

      <div className={styles.previewScrollArea}>
        <div className={styles.previewSurface} data-theme="light">
          <div className={styles.previewBanner}>Preview mode — responses are not saved.</div>

          {hasContent ? (
            <>
              <SurveyHeader
                eyebrow="Survey preview"
                publication={publication}
                practiceArea="Practice area"
                firmName={firmName}
              />
              <SurveyForm
                key={surveyFormKey}
                definition={previewDefinitionWithRandom as unknown as Record<string, unknown>}
                locale="en"
                onSubmitAction={previewSubmitAction}
                labels={{
                  submittedTitle: "Preview submitted",
                }}
              />
            </>
          ) : (
            <div className={styles.empty}>
              No questions match the current context. Adjust the filters above to see questions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
